<script setup lang="ts">
import { compact, every, flatten, some } from 'lodash'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import ErrorModal from '@/components/common/ErrorModal.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import SparkBreadcrumb from '@/components/generic/SparkBreadcrumb.vue'
import { PriceUnit } from '@/config/unit'
import { convertToExcel } from '@/helpers/excel-addin/convertToExcel'
import { analyticsTrack } from '@/plugins/usage-analytics'
import { ROUTE_NAMES } from '@/router/route-names'
import { useMainStore } from '@/stores'
import { useExcelAddinStore } from '@/stores/excel-addin'
import { VesselType } from '@/types/excel-addin'
import type { components } from '#/types/capi-generated'

import { generateId } from './utils'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'

type Contract = components['schemas']['Contract']

const priceReleasesOptions = [
  { text: 'Latest', value: 'latest' },
  { text: 'Release Date', value: 'date' },
  { text: 'Last n Releases', value: 'last-n' },
  { text: 'All', value: 'all' }
]

const lastNReleaseNumber = ref<number | null>(30)

type StateContract = Contract & {
  checked: boolean
  groupLabel: string
}

const state = reactive<{
  contracts: null | StateContract[]
  priceUnit: PriceUnit
  priceReleasesFilter: 'latest' | 'all' | 'date' | 'last-n'
  releaseDate: dayjs.Dayjs
  submitting: boolean
  errors: string[]
  showErrorModal: boolean
}>({
  priceUnit: PriceUnit.USD_PER_MMBTU,
  priceReleasesFilter: 'latest',
  releaseDate: dayjs(),
  contracts: null,
  submitting: false,
  errors: [] as string[],
  showErrorModal: false
})

const excelAddinStore = useExcelAddinStore()
const mainStore = useMainStore()

const loading = computed(() => {
  return !excelAddinStore.contracts
})

const allChecked = computed(() => {
  return !!state.contracts?.length && every(state.contracts, (c: StateContract) => c.checked)
})

const someChecked = computed(() => {
  return some(state.contracts, (c: StateContract) => c.checked)
})

const onCheckAllChange = (e: any) => {
  if (e.target.checked) {
    state.contracts?.forEach((c) => (c.checked = true))
  } else {
    state.contracts?.forEach((c) => (c.checked = false))
  }
}

const GROUP_LABELS: Record<string, string> = {
  sparknwe: 'SparkNWE',
  sparkswe: 'SparkSWE',
  'sparknwe-fin': 'SparkNWE-Fin',
  'sparkswe-fin': 'SparkSWE-Fin',
}

function loadContracts() {
  if (!excelAddinStore.contracts) {
    return
  }

  const productContracts = excelAddinStore.contractsByProducts['lng-basis'] ?? []
  state.contracts = productContracts.map((c) => ({
    ...c,
    checked: false,
    groupLabel: GROUP_LABELS[c.contractGroupName] ?? c.contractGroupName
  }))
}

const groupedContracts = computed(() => {
  if (!state.contracts) return [] as { label: string; contracts: StateContract[] }[]

  const groups = new Map<string, StateContract[]>()
  state.contracts.forEach((c) => {
    const label = c.groupLabel
    if (!groups.has(label)) {
      groups.set(label, [])
    }
    groups.get(label)!.push(c)
  })

  return Array.from(groups.entries()).map(([label, contracts]) => {
    return { label, contracts }
  })
})

async function loadPriceReleases() {
  state.submitting = true
  state.errors = []

  try {
    if (!state.contracts) {
      return
    }

    const selectedContracts = state.contracts.filter((c) => c.checked)

    const results = await Promise.allSettled(
      selectedContracts.map(async (c) => {
        let priceReleases
        if (state.priceReleasesFilter === 'latest') {
          priceReleases = [
            await excelAddinStore.fetchLatestPriceRelease(c.id, VesselType.DEFAULT)
          ]
        } else if (state.priceReleasesFilter === 'date') {
          priceReleases = await excelAddinStore.fetchPriceReleaseByReleaseDate(
            c.id,
            state.releaseDate.toDate(),
            VesselType.DEFAULT
          )
        } else if (state.priceReleasesFilter === 'all') {
          priceReleases = await excelAddinStore.fetchAllPriceReleases(c.id, VesselType.DEFAULT)
        } else if (state.priceReleasesFilter === 'last-n') {
          const lastN = Math.max(1, Number(lastNReleaseNumber.value ?? 1))
          priceReleases = await excelAddinStore.fetchPriceReleases(
            c.id,
            VesselType.DEFAULT,
            lastN
          )
        }

        return {
          ...c,
          priceReleases
        }
      })
    )

    const priceReleasesByContracts: (StateContract & { priceReleases: any })[] = []

    results.forEach((result, index) => {
      const contract = selectedContracts[index]
      if (result.status === 'rejected') {
        const err = result.reason
        let errorMessage = ''
        if (err instanceof AxiosError) {
          if (err.status === 404) {
            if (state.priceReleasesFilter === 'date') {
              errorMessage = `No price release found for contract "${contract.fullName}" (${contract.tickerSymbol}) on release date ${state.releaseDate.format('YYYY-MM-DD')}`
            } else if (state.priceReleasesFilter === 'latest') {
              errorMessage = `No latest price release found for contract "${contract.fullName}" (${contract.tickerSymbol})`
            } else {
              errorMessage = `No price releases found for contract "${contract.fullName}" (${contract.tickerSymbol})`
            }
          } else {
            errorMessage = `Error fetching contract "${contract.fullName}" (${contract.tickerSymbol}): ${err.message}`
          }
        } else if (err instanceof Error) {
          errorMessage = `Error fetching contract "${contract.fullName}" (${contract.tickerSymbol}): ${err.message}`
        } else {
          errorMessage = `Unknown error fetching contract "${contract.fullName}" (${contract.tickerSymbol})`
        }
        if (errorMessage) {
          state.errors.push(errorMessage)
        }
      } else {
        priceReleasesByContracts.push(result.value)
      }
    })

    if (priceReleasesByContracts.length > 0) {
      const rangeValues = compact(
        flatten(
          priceReleasesByContracts.map((c) => {
            if (c.priceReleases) {
              return convertToExcel(c, compact(c.priceReleases), state.priceUnit)
            }
          })
        )
      )
      const successfulContracts = priceReleasesByContracts as StateContract[]
      await pasteTableOntoExcel(successfulContracts, rangeValues)
    }
  } finally {
    if (state.errors.length > 0) {
      state.showErrorModal = true
    }
    state.submitting = false
  }
}

const TABLE_COLUMNS = [
  'release date',
  'contract',
  'Sub Contract',
  'Sub Contract Type',
  'Period Name',
  'Period Start',
  'Period End',
  'Calendar Month',
  'You',
  'Spark',
  'Spark Min',
  'Spark Max',
  'Portfolio Player',
  'Portfolio Player Min',
  'Portfolio Player Max',
  'Ship Owner',
  'Ship Owner Min',
  'Ship Owner Max'
]

const COLUMNS_COUNT = TABLE_COLUMNS.length
const MAX_CELLS_PER_OPERATION = 1000000

async function pasteTableOntoExcel(
  selectedContracts: StateContract[],
  rangeValues: (string | number)[][]
) {
  try {
    if (!window.Excel) {
      console.warn('Excel is not loaded')
      return
    }

    const ROWS_COUNT = rangeValues.length
    const totalCells = ROWS_COUNT * COLUMNS_COUNT

    if (totalCells > MAX_CELLS_PER_OPERATION) {
      console.warn(`Large dataset detected: ${totalCells} cells. Consider splitting into smaller batches.`)
    }

    await window.Excel.run(async (context) => {
      const app = context.workbook.application
      app.suspendScreenUpdatingUntilNextSync()
      app.suspendApiCalculationUntilNextSync()

      const workbook = context.workbook
      const sheet = workbook.worksheets.getActiveWorksheet()
      const activeRange = workbook.getSelectedRange()
      const firstCell = activeRange.getCell(0, 0)

      sheet.load('tables')
      activeRange.load('address')
      await context.sync()

      const existingTableCollection = sheet.tables
      const existingTables = existingTableCollection.items

      const tableDataRange = firstCell.getAbsoluteResizedRange(
        ROWS_COUNT + 1,
        COLUMNS_COUNT
      )

      if (existingTables && existingTables.length > 0) {
        const tablesToDelete: string[] = []
        const tableIntersections: { table: Excel.Table; intersection: Excel.Range }[] = []

        for (const existingTable of existingTables) {
          const range = existingTable.getRange()
          const intersection = range.getIntersectionOrNullObject(tableDataRange)
          intersection.load('address')
          tableIntersections.push({ table: existingTable, intersection })
        }

        await context.sync()

        for (const { table, intersection } of tableIntersections) {
          if (!intersection.isNullObject) {
            tablesToDelete.push(table.name)
          }
        }

        for (const tableName of tablesToDelete) {
          try {
            sheet.tables.getItem(tableName).delete()
          } catch (err) {
            state.errors.push(`Error deleting table ${tableName}: ${JSON.stringify(err)}`)
          }
        }

        if (tablesToDelete.length > 0) {
          await context.sync()
        }
      }

      const allTableData = [TABLE_COLUMNS, ...rangeValues]
      tableDataRange.values = allTableData

      const TABLE_NAME = 'CargoTable' + generateId()
      const contractsTable = sheet.tables.add(tableDataRange, true)
      contractsTable.name = TABLE_NAME

      const headerRange = contractsTable.getHeaderRowRange()
      const dataRange = contractsTable.getDataBodyRange()

      contractsTable.style = 'TableStyleLight1'

      headerRange.format.fill.color = '#4F41F4'
      const headerFont = headerRange.format.font
      headerFont.color = '#FFFFFF'
      headerFont.size = 12
      headerFont.name = 'Mont'
      headerFont.bold = true

      dataRange.format.fill.color = '#FFFFFF'
      const dataFont = dataRange.format.font
      dataFont.color = '#000000'
      dataFont.name = 'Roboto'
      dataFont.bold = false

      if (Office.context.requirements.isSetSupported('ExcelApi', '1.2')) {
        const usedRange = sheet.getUsedRange()
        const usedRangeFormat = usedRange.format
        usedRangeFormat.autofitColumns()
        usedRangeFormat.autofitRows()
      }

      sheet.activate()
      await context.sync()
    })

    setTimeout(() => {
      analyticsTrack('RUN_EXCEL_ADDIN', {
        contracts: selectedContracts.map((c) => c.id),
        priceUnit: state.priceUnit,
        priceReleasesFilter: state.priceReleasesFilter
      })
    }, 0)
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err)
    state.errors.push(msg)
  }
}

const router = useRouter()

function handleBreadcrumbInput(value: string) {
  router.push({ name: value })
}

onMounted(() => {
  loadContracts()
})

watch(
  () => excelAddinStore.contracts,
  () => {
    loadContracts()
  }
)
</script>

<template>
  <Loader :modelValue="mainStore.isLoading">
    <div class="cargo-page p-5 space-y-10">
      <section class="space-y-6">
        <PageHeader title="Get LNG Cargo Pricing Data" />
        <SparkBreadcrumb
          :config="[
            { name: 'Products', icon: 'home-filled', value: ROUTE_NAMES.HOME },
            {
              name: 'LNG Cargo',
              icon: 'basis',
              styleClasses: 'bg-basis text-white'
            }
          ]"
          @input="handleBreadcrumbInput"
        />
      </section>
      <a-form layout="vertical">
        <a-form-item label="Please select the contracts to include">
          <div class="px-3 py-1 border-1 border-gray-300 bg-white">
            <table class="table-fixed w-full theme-table">
              <thead>
                <tr>
                  <th class="text-left">Contract Id</th>
                  <th class="text-right w-[1rem]">
                    <a-checkbox
                      :checked="allChecked"
                      @change="onCheckAllChange"
                      :disabled="loading"
                    >
                    </a-checkbox>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in groupedContracts" :key="group.label">
                  <tr class="bg-gray-50">
                    <td colspan="2" class="py-2 font-semibold text-gray-900">
                      {{ group.label }}
                    </td>
                  </tr>
                  <tr
                    v-for="(contract, $index) in group.contracts"
                    class="hover:bg-basis-50 cursor-pointer"
                    :key="contract.id + '-' + $index"
                    @click="contract.checked = !contract.checked"
                  >
                    <td>{{ contract.id }}</td>
                    <td class="text-right">
                      <a-checkbox :checked="contract.checked" @click.stop="contract.checked = !contract.checked"></a-checkbox>
                    </td>
                  </tr>
                </template>
                <tr v-if="!state.contracts">
                  <td colspan="2">
                    <div class="py-5 text-center text-gray-600">
                      Loading contracts...
                    </div>
                  </td>
                </tr>
                <tr v-else-if="!state.contracts.length">
                  <td colspan="2">
                    <div class="py-5 text-center text-gray-600">
                      No contracts found
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </a-form-item>

        <a-form-item :colon="false" label="Price Unit">
          <a-radio-group
            v-model:value="state.priceUnit"
            button-style="solid"
            size="small"
          >
            <a-radio-button :value="PriceUnit.USD_PER_MMBTU">$/MMBtu</a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item :colon="false" label="Price Releases">
          <a-radio-group
            class="w-full"
            v-model:value="state.priceReleasesFilter"
            :disabled="loading"
            size="small"
          >
            <a-radio-button
              v-for="opt in priceReleasesOptions"
              :value="opt.value"
              :key="opt.value"
              >{{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item
          v-if="state.priceReleasesFilter === 'last-n'"
          :colon="false"
          label="Number of releases"
        >
          <a-input-number
            class="w-full"
            v-model:value="lastNReleaseNumber"
            :min="1"
            :precision="0"
            :disabled="loading"
            size="small"
          />
        </a-form-item>
        <a-form-item
          v-if="state.priceReleasesFilter === 'date'"
          :colon="false"
          label="Release Date"
        >
          <a-date-picker
            class="w-full"
            v-model:value="state.releaseDate"
            :allowClear="false"
            @change="(d: dayjs.Dayjs | null) => (state.releaseDate = d ?? dayjs())"
            :disabled="loading"
            format="ddd, DD MMM YYYY"
            size="small"
          />
        </a-form-item>
        <div>
          <SparkButton
            class="w-full"
            size="xl"
            color="basis"
            :disabled="!someChecked || loading"
            :loading="state.submitting"
            @click="loadPriceReleases"
            >Insert data into selected cell
          </SparkButton>
        </div>
      </a-form>

      <ErrorModal
        v-model:visible="state.showErrorModal"
        title="Some contracts could not be retrieved"
        description="The following contracts encountered issues."
        :errors="state.errors"
      />
    </div>
  </Loader>
</template>
<style lang="scss">
.cargo-page {
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: #4F41F4 !important;
    border-color: #4F41F4 !important;

    &:hover {
      background: #473BDC !important;
      border-color: #473BDC !important;
    }

    &::before {
      background-color: #4F41F4 !important;
    }
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #4F41F4 !important;
    border-color: #4F41F4 !important;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: #4F41F4 !important;
  }
}

.theme-table {
  thead th {
    @apply py-2 border-b-1 border-black;
  }

  tbody td {
    @apply py-1;
  }
}
</style>
